import React, { useState, useEffect, useContext } from "react";
import { Container, Form, Button, Spinner } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import api from "../api";
import LocationSelect from "../components/LocationSelect";
import {
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCrown,
} from "react-icons/fa";

const normalizePhoneValue = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const digits = trimmed.replace(/[^0-9]/g, "");
  if (!digits) return "";
  return trimmed.startsWith("+") ? `+${digits}` : digits;
};

export default function Profile() {
  const { user, login } = useContext(AuthContext);
  const [phoneVal, setPhoneVal] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [cityVal, setCityVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [primeLoading, setPrimeLoading] = useState(false);
  const primePaymentUrl =
    process.env.REACT_APP_PRIME_PAYMENT_URL || "https://rzp.io/l/rentalo-prime";

  // Load user on mount or when context user changes
  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setEmail(user.email || "");
      setAadhaar(user.aadhaar || "");
      setStateVal(user.state || "");
      setCityVal(user.city || "");
      setPhoneVal(user.phone || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    // Validate required fields
    if (!phoneVal.trim()) {
      setError("Phone number is required");
      setLoading(false);
      return;
    }

    if (!user?._id) {
      setError("Unable to update profile right now. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const normalizedPhone = normalizePhoneValue(phoneVal);
      if (!normalizedPhone) {
        setError("Please enter a valid phone number");
        setLoading(false);
        return;
      }

      const res = await api.post("/auth/complete-profile", {
        userId: user?._id,
        phone: normalizedPhone,
        username,
        email,
        aadhaar,
        state: stateVal,
        city: cityVal,
      });

      if (res.data.success) {
        setMessage(res.data.message || "Profile updated successfully");
        // Update context with new user data
        login(res.data.user);
      } else {
        setError(res.data.message || "Failed to update profile");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to update profile. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Geolocation and reverse geocoding for address
  const handleUseLocation = async () => {
    setLocationError("");
    setLocating(true);
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser.");
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          // Nominatim reverse geocoding
          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`;
          const res = await fetch(url);
          const data = await res.json();
          const address = data.address || {};
          const city =
            address.city ||
            address.town ||
            address.village ||
            address.hamlet ||
            "";
          const state = address.state || "";
          if (!city || !state) {
            setLocationError(
              "Could not determine your city/state from your location."
            );
            setLocating(false);
            return;
          }
          setCityVal(city);
          setStateVal(state);
        } catch (err) {
          setLocationError("Failed to reverse-geocode your location.");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocationError(
          "Failed to get your location. Please allow location access."
        );
        setLocating(false);
      }
    );
  };

  const handlePurchasePrime = async () => {
    if (!user?._id) {
      setError("Please log in again to upgrade your membership.");
      return;
    }

    setError("");
    setMessage("");
    setPrimeLoading(true);

    try {
      const res = await api.post("/auth/purchase-prime", {
        userId: user._id,
      });

      if (res.data?.success && res.data?.user) {
        login(res.data.user);
        setMessage(
          res.data?.message ||
            "Prime membership activated! Your listings now appear above standard sellers."
        );
      } else {
        setError(
          res.data?.message ||
            "Unable to activate prime membership right now. Please try again."
        );
      }
    } catch (err) {
      console.error("Prime purchase error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to activate prime membership. Please try again."
      );
    } finally {
      setPrimeLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-7">
          <div className="card shadow-lg border-0 rounded-4 p-4 profile-card">
            <div className="text-center mb-4">
              <div className="mb-3">
                <FaUserCircle size={80} className="text-primary" />
              </div>
              <h2 className="fw-bold text-gradient mb-1">
                {user?.username || "User"}
              </h2>
              <p className="text-muted mb-0">Manage your profile information</p>
            </div>

            {error && (
              <div className="alert alert-danger rounded-pill text-center">
                {error}
              </div>
            )}
            {message && (
              <div className="alert alert-success rounded-pill text-center">
                {message}
              </div>
            )}

            <Form onSubmit={submit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  <FaEnvelope className="me-2 text-primary" /> Email
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-pill"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  <FaPhone className="me-2 text-primary" /> Phone
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={phoneVal}
                  onChange={(event) => {
                    const next = event.target.value;
                    // Allow digits, +, spaces, and hyphen for basic formatting
                    setPhoneVal(next.replace(/[^0-9+\s-]/g, ""));
                  }}
                  className="rounded-pill"
                  inputMode="tel"
                  maxLength={15}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Username</Form.Label>
                <Form.Control
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="rounded-pill"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Aadhaar Number</Form.Label>
                <Form.Control
                  type="text"
                  value={aadhaar}
                  onChange={(e) => setAadhaar(e.target.value)}
                  className="rounded-pill"
                  placeholder="Enter your Aadhaar number"
                />
              </Form.Group>

              <div className="mb-3">
                <Form.Label className="fw-semibold">
                  <FaMapMarkerAlt className="me-2 text-primary" /> Location
                </Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Button
                    variant="outline-primary"
                    onClick={handleUseLocation}
                    disabled={locating}
                    className="rounded-pill"
                  >
                    {locating ? "Detecting..." : "Use My Location"}
                  </Button>
                </div>
                {locationError && (
                  <div className="alert alert-warning rounded-pill text-center small">
                    {locationError}
                  </div>
                )}
                {cityVal && stateVal && !locationError && (
                  <div className="alert alert-success rounded-pill text-center small">
                    Detected:{" "}
                    <strong>
                      {cityVal}, {stateVal}
                    </strong>
                  </div>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">City</Form.Label>
                <Form.Control
                  type="text"
                  value={cityVal}
                  onChange={(e) => setCityVal(e.target.value)}
                  className="rounded-pill"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">State</Form.Label>
                <LocationSelect
                  value={stateVal}
                  onChange={setStateVal}
                  className="rounded-pill"
                />
              </Form.Group>

              <Button
                type="submit"
                variant="primary"
                className="w-100 rounded-pill fw-bold py-2 cta-btn"
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Form>

            <div className="mt-4" id="prime-membership">
              <div className="p-4 rounded-4 border border-warning-subtle bg-warning-subtle bg-opacity-25 text-center">
                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                  <FaCrown className="text-warning" size={28} />
                  <h5 className="mb-0 fw-bold">Rentalo Prime Membership</h5>
                </div>
                {user?.isPrime ? (
                  <>
                    <p className="mb-2 text-secondary">
                      You are a Prime member. Your listings automatically float
                      above standard sellers for better visibility.
                    </p>
                    {user?.primePurchasedAt && (
                      <p className="mb-3 small text-muted">
                        Active since{" "}
                        {new Date(user.primePurchasedAt).toLocaleDateString(
                          "en-IN",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </p>
                    )}
                    <Button
                      variant="success"
                      className="rounded-pill px-4 fw-semibold"
                      disabled
                    >
                      Prime Activated
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="mb-3 text-secondary">
                      Boost your marketplace and room listings so they appear
                      before other sellers. A one-time upgrade for just ₹100
                      helps you close deals faster.
                    </p>
                    <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                      <Button
                        as="a"
                        href={primePaymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="warning"
                        className="rounded-pill px-4 fw-semibold"
                      >
                        Buy Prime for ₹100
                      </Button>
                      <Button
                        variant="outline-warning"
                        className="rounded-pill px-4 fw-semibold"
                        onClick={handlePurchasePrime}
                        disabled={primeLoading}
                      >
                        {primeLoading ? (
                          <span className="d-inline-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" />
                            <span>Activating...</span>
                          </span>
                        ) : (
                          "Activate Prime"
                        )}
                      </Button>
                    </div>
                    <p className="mt-2 mb-0 text-muted small">
                      Complete the payment first, then click Activate Prime to
                      unlock your benefits.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
